// ImportanceAndBenefits.jsx
import React from 'react';
import { TrendingUp, Users, Scale, DollarSign, Shield, AlertTriangle, BarChart3, UserCheck } from 'lucide-react';
import './ImportanceAndBenefits.css';

const ImportanceAndBenefits = ({ data, onChange, validationErrors = {} }) => {
  const handleMultiSelect = (field, value) => {
    const currentValues = data[field] || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    onChange(field, newValues);
  };

  const handleOtherFieldChange = (field, value) => {
    onChange(field, value);
  };

  // Helper function to get error class
  const getErrorClass = (fieldName) => {
    return validationErrors[fieldName] ? 'error' : '';
  };

  // Helper function to render error message
  const renderError = (fieldName) => {
    if (validationErrors[fieldName]) {
      return (
        <div className="error-message" role="alert">
          {validationErrors[fieldName]}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="step-content">
      <div className="step-header-section">
        {/* <TrendingUp className="section-icon" size={32} /> */}
        <h2>ثانياً: أهمية ومزايا الإفصاح عن البيانات الشخصية</h2>
      </div>
      
      {/* Data Importance Question */}
      <div className="">
        <div className="question-header">
          <span className="question-number">7</span>
          <label className="question-title">
            ما مدى أهمية حصول شركتكم على البيانات الشخصية للمترشحين للتعيين على الوظائف؟
            <span className="required-indicator">*</span>
          </label>
        </div>
        
        <div className="data-category">
          {/* <div className="category-header">
            <BarChart3 className="category-icon" size={24} />
            <h4>مستوى الأهمية:</h4>
          </div> */}
          
          <div className={`radio-options-grid ${getErrorClass('dataImportance')}`}>
            {[
              { value: 'مهمة جداً', icon: '🔴', level: 'عالية جداً' },
              { value: 'متوسطة الأهمية', icon: '🟡', level: 'متوسطة' },
              { value: 'قليلة الأهمية', icon: '🟠', level: 'منخفضة' },
              { value: 'غير مهمة على الإطلاق', icon: '⚪', level: 'غير مهمة' }
            ].map(option => (
              <label key={option.value} className="radio-option importance-option">
                <input
                  type="radio"
                  name="dataImportance"
                  value={option.value}
                  checked={data.dataImportance === option.value}
                  onChange={(e) => onChange('dataImportance', e.target.value)}
                />
                <span className="radio-custom" aria-hidden="true"></span>
                <div className="option-content">
                  <div className="option-text-group">
                    <span className="radio-text">{option.value}</span>
                    <span className="option-description">{option.level}</span>
                  </div>
                </div>
              </label>
            ))}
          </div>
          {renderError('dataImportance')}
        </div>
      </div>

      {/* Benefits Question */}
      <div className="">
        <div className="question-header">
          <span className="question-number">8</span>
          <label className="question-title">
            حدد أهم المزايا التي تحققها شركتكم من افصاح المترشحين عن بياناتهم الشخصية؟ 
            <span className="multiple-choice-note">(يمكن اختيار أكثر من إجابة)</span>
          </label>
        </div>
        
        {/* Operational Benefits */}
        <div className="data-category" data-field="operationalBenefits"> 
          <div className="category-header">
            <Users className="category-icon" size={24} />
            <h4>المزايا التشغيلية والإدارية:</h4>
          </div>
          
          <div className={`checkbox-grid ${getErrorClass('operationalBenefits')}`}>
            {[
              'تحسين دقة عملية الاختيار والتوظيف',
              'تسريع إجراءات التعيين والتهيئة المبدئية',
              'ضمان التطابق بين قدرات المرشح ومتطلبات الوظيفة',
              'تسهيل التخطيط للموارد البشرية المستقبلية',
              'تحسين كفاءة عمليات الإدماج والتوجيه',
              'ضمان استمرارية الأعمال والعمليات',
              'غير مهم'
            ].map(benefit => (
              <label key={benefit} className="checkbox-item">
                <input
                  type="checkbox"
                  checked={(data.operationalBenefits || []).includes(benefit)}
                  onChange={() => handleMultiSelect('operationalBenefits', benefit)}
                />
                <span className="checkbox-custom" aria-hidden="true"></span>
                <span className="checkbox-text">{benefit}</span>
              </label>
            ))}
          </div>
          {renderError('operationalBenefits')}
          
          <div className="other-input-group">
            <label className="other-label" htmlFor="other-operational">أخرى (يرجى ذكرها):</label>
            <input
              id="other-operational"
              type="text"
              value={data.otherOperationalBenefits || ''}
              onChange={(e) => handleOtherFieldChange('otherOperationalBenefits', e.target.value)}
              placeholder="مزايا تشغيلية أخرى..."
              className={`other-input ${getErrorClass('otherOperationalBenefits')}`}
            />
            {renderError('otherOperationalBenefits')}
          </div>
        </div>

        {/* Legal Benefits */}
        <div className="data-category" data-field="legalBenefits">
          <div className="category-header">
            <Scale className="category-icon" size={24} />
            <h4>المزايا القانونية والتنظيمية:</h4>
          </div>
          
          <div className={`checkbox-grid ${getErrorClass('legalBenefits')}`}>
            {[
              'ضمان الامتثال للقوانين واللوائح المحلية',
              'تجنب المخاطر القانونية والعقوبات',
              'التحقق من صحة ودقة المعلومات المقدمة',
              'ضمان النزاهة والشفافية في التوظيف',
              'تلبية متطلبات الجهات التنظيمية والرقابية',
              'حماية حقوق الشركة والموظفين',
              'غير مهم'
            ].map(benefit => (
              <label key={benefit} className="checkbox-item">
                <input
                  type="checkbox"
                  checked={(data.legalBenefits || []).includes(benefit)}
                  onChange={() => handleMultiSelect('legalBenefits', benefit)}
                />
                <span className="checkbox-custom" aria-hidden="true"></span>
                <span className="checkbox-text">{benefit}</span>
              </label>
            ))}
          </div>
          {renderError('legalBenefits')}
          
          <div className="other-input-group">
            <label className="other-label" htmlFor="other-legal">أخرى (يرجى ذكرها):</label>
            <input
              id="other-legal"
              type="text"
              value={data.otherLegalBenefits || ''}
              onChange={(e) => handleOtherFieldChange('otherLegalBenefits', e.target.value)}
              placeholder="مزايا قانونية أخرى..."
              className={`other-input ${getErrorClass('otherLegalBenefits')}`}
            />
            {renderError('otherLegalBenefits')}
          </div>
        </div>

        {/* Financial Benefits */}
        <div className="data-category" data-field="financialBenefits">
          <div className="category-header">
            <DollarSign className="category-icon" size={24} />
            <h4>المزايا المالية والاقتصادية:</h4>
          </div>
          
          <div className={`checkbox-grid ${getErrorClass('financialBenefits')}`}>
            {[
              'تقليل تكاليف: التوظيف / التدريب/ دوران الموظفين/ الاستقالات/ التأمينات والمخاطر',
              'تحسين الإنتاجية والأداء العام',
              'تقليل مخاطر الأخطاء والإهمال المهني',
              'تحسين العائد على الاستثمار في رأس المال البشري',
              'غير مهم'
            ].map(benefit => (
              <label key={benefit} className="checkbox-item">
                <input
                  type="checkbox"
                  checked={(data.financialBenefits || []).includes(benefit)}
                  onChange={() => handleMultiSelect('financialBenefits', benefit)}
                />
                <span className="checkbox-custom" aria-hidden="true"></span>
                <span className="checkbox-text">{benefit}</span>
              </label>
            ))}
          </div>
          {renderError('financialBenefits')}
          
          <div className="other-input-group">
            <label className="other-label" htmlFor="other-financial">أخرى (يرجى ذكرها):</label>
            <input
              id="other-financial"
              type="text"
              value={data.otherFinancialBenefits || ''}
              onChange={(e) => handleOtherFieldChange('otherFinancialBenefits', e.target.value)}
              placeholder="مزايا مالية أخرى..."
              className={`other-input ${getErrorClass('otherFinancialBenefits')}`}
            />
            {renderError('otherFinancialBenefits')}
          </div>
        </div>

        {/* Security Benefits */}
        <div className="data-category" data-field="securityBenefits">
          <div className="category-header">
            <Shield className="category-icon" size={24} />
            <h4>المزايا الأمنية والمؤسسية:</h4>
          </div>
          
          <div className={`checkbox-grid ${getErrorClass('securityBenefits')}`}>
            {[
              'حماية الاصول والمعلومات الحساسة للشركة',
              'تعزيز الأمان الداخلي وبيئة العمل الآمنة',
              'بناء فريق عمل موثوق ومتجانس',
              'تحسين سمعة الشركة ومكانتها في السوق',
              'تعزيز ثقة العملاء والشركاء التجاريين',
              'ضمان استدامة واستقرار الأعمال',
              'غير مهم'
            ].map(benefit => (
              <label key={benefit} className="checkbox-item">
                <input
                  type="checkbox"
                  checked={(data.securityBenefits || []).includes(benefit)}
                  onChange={() => handleMultiSelect('securityBenefits', benefit)}
                />
                <span className="checkbox-custom" aria-hidden="true"></span>
                <span className="checkbox-text">{benefit}</span>
              </label>
            ))}
          </div>
          {renderError('securityBenefits')}
          
          <div className="other-input-group">
            <label className="other-label" htmlFor="other-security">أخرى (يرجى ذكرها):</label>
            <input
              id="other-security"
              type="text"
              value={data.otherSecurityBenefits || ''}
              onChange={(e) => handleOtherFieldChange('otherSecurityBenefits', e.target.value)}
              placeholder="مزايا أمنية أخرى..."
              className={`other-input ${getErrorClass('otherSecurityBenefits')}`}
            />
            {renderError('otherSecurityBenefits')}
          </div>
        </div>
      </div>

      {/* Data Impact Question */}
      <div className="">
        <div className="question-header" >
          <span className="question-number">9</span>
          <label className="question-title">
            تؤثر جودة واكتمال البيانات الشخصية على قرارات التعيين في شركتنا بشكل:
            <span className="required-indicator">*</span>
          </label>
        </div>
        
        <div className="data-category">
          {/* <div className="category-header">
            <BarChart3 className="category-icon" size={24} />
            <h4>مستوى التأثير:</h4>
          </div> */}
          
          <div className={`radio-options-grid ${getErrorClass('dataImpact')}`}>
            {[
              { value: 'حاسم في جميع القرارات', icon: '🔴', level: 'تأثير كامل' },
              { value: 'كبير في معظم القرارات', icon: '🟠', level: 'تأثير كبير' },
              { value: 'متوسط حسب نوع الوظيفة', icon: '🟡', level: 'تأثير متغير' },
              { value: 'محدود في بعض الحالات', icon: '🟢', level: 'تأثير محدود' },
              { value: 'لا يوجد أي تأثير', icon: '⚪', level: 'بدون تأثير' }
            ].map(option => (
              <label key={option.value} className="radio-option impact-option">
                <input
                  type="radio"
                  name="dataImpact"
                  value={option.value}
                  checked={data.dataImpact === option.value}
                  onChange={(e) => onChange('dataImpact', e.target.value)}
                />
                <span className="radio-custom" aria-hidden="true"></span>
                <div className="option-content">
                  <div className="option-text-group">
                    <span className="radio-text">{option.value}</span>
                    <span className="option-description">{option.level}</span>
                  </div>
                </div>
              </label>
            ))}
          </div>
          {renderError('dataImpact')}
        </div>
      </div>

      {/* Cooperation Level Question */}
      <div className="">
        <div className="question-header">
          <span className="question-number">10</span>
          <label className="question-title">
            حدد مستوى تعاون المترشحين للتعيين في تقديم بياناتهم الشخصية المطلوبة:
            <span className="required-indicator">*</span>
          </label>
        </div>
        
        <div className="data-category">
          {/* <div className="category-header">
            <UserCheck className="category-icon" size={24} />
            <h4>مستوى التعاون:</h4>
          </div> */}
          
          <div className={`radio-options-grid ${getErrorClass('cooperationLevel')}`}>
            {[
              { value: 'تعاون كامل من جميع المترشحين', icon: '🟢', level: '100% تعاون' },
              { value: 'تعاون جيد من معظم المترشحين', icon: '🟡', level: 'تعاون جيد' },
              { value: 'تعاون متوسط مع بعض التحفظات', icon: '🟠', level: 'تعاون متوسط' },
              { value: 'تعاون محدود ومقاومة من البعض', icon: '🔴', level: 'تعاون ضعيف' },
              { value: 'عدم تعاون', icon: '⚫', level: 'بدون تعاون' }
            ].map(option => (
              <label key={option.value} className="radio-option cooperation-option">
                <input
                  type="radio"
                  name="cooperationLevel"
                  value={option.value}
                  checked={data.cooperationLevel === option.value}
                  onChange={(e) => onChange('cooperationLevel', e.target.value)}
                />
                <span className="radio-custom" aria-hidden="true"></span>
                <div className="option-content">
                  <div className="option-text-group">
                    <span className="radio-text">{option.value}</span>
                    <span className="option-description">{option.level}</span>
                  </div>
                </div>
              </label>
            ))}
          </div>
          {renderError('cooperationLevel')}
        </div>
      </div>

      {/* Risks Question */}
      <div className="">
        <div className="question-header">
          <span className="question-number">11</span>
          <label className="question-title">
            حدد المخاطر التي تواجه شركتكم نتيجة عدم افصاح المترشحين للتعيين عن بياناتهم الشخصية؟ 
            <span className="multiple-choice-note">(يمكن اختيار أكثر من إجابة)</span>
          </label>
        </div>
        
        <div className="data-category" data-field="nonDisclosureRisks">
          {/* <div className="category-header">
            <AlertTriangle className="category-icon" size={24} />
            <h4>المخاطر المحتملة:</h4>
          </div> */}
          
          <div className={`checkbox-grid ${getErrorClass('nonDisclosureRisks')}`}>
            {[
              'توظيف أشخاص غير مؤهلين أو مناسبين',
              'اكتشاف مشاكل أو قضايا بعد التعيين',
              'تضارب مصالح لم يتم الكشف عنه',
              'مؤهلات مزورة أو غير صحيحة',
              'تسريب معلومات سرية أو حساسة',
              'مخالفات أو قضايا قانونية أو تنظيمية للمترشح',
              'مشاكل مع الجهات الأمنية',
              'أخطاء مهنية أو تشغيلية جسيمة',
              'خسائر مالية مباشرة',
              'توقف أو تأخير في عمليات التوظيف',
              'مشاكل مع العملاء أو الموردين',
              'غير مهم'
            ].map(risk => (
              <label key={risk} className="checkbox-item risk-item">
                <input
                  type="checkbox"
                  checked={(data.nonDisclosureRisks || []).includes(risk)}
                  onChange={() => handleMultiSelect('nonDisclosureRisks', risk)}
                />
                <span className="checkbox-custom" aria-hidden="true"></span>
                <span className="checkbox-text">{risk}</span>
              </label>
            ))}
          </div>
          {renderError('nonDisclosureRisks')}
          
          <div className="other-input-group">
            <label className="other-label" htmlFor="other-risks">أخرى (يرجى ذكرها):</label>
            <input
              id="other-risks"
              type="text"
              value={data.otherRisks || ''}
              onChange={(e) => handleOtherFieldChange('otherRisks', e.target.value)}
              placeholder="مخاطر أخرى..."
              className={`other-input ${getErrorClass('otherRisks')}`}
            />
            {renderError('otherRisks')}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportanceAndBenefits;